import {
  parsePhoneNumber,
  getCountryCallingCode,
  getCountries,
} from 'react-phone-number-input/max';
import { readCompanyPreferences } from '../../utils/companyPreferencesStorage';

function getDefaultCountry() {
  try {
    let companyId = null;
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      companyId = userObj?.company_id;
    }
    if (!companyId) companyId = localStorage.getItem('company_id');

    if (companyId) {
      const prefs = readCompanyPreferences(companyId);
      if (prefs && prefs.country) return prefs.country;
    }
  } catch {
    // Ignore
  }
  return 'IN';
}

const getIsoCodeFromCallingCode = (callingCode) => {
  const defaultCountry = getDefaultCountry();
  if (!callingCode) return defaultCountry;
  const digits = String(callingCode).replace(/[^\d]/g, '');
  const match = getCountries().find(
    (country) => getCountryCallingCode(country) === digits,
  );
  return match || defaultCountry;
};

const formatLocalPhoneGroups = (digits) => {
  if (!digits) return '';
  if (digits.length <= 1) return digits;
  const splitIndex = Math.floor(digits.length / 2);
  return `${digits.slice(0, splitIndex)} ${digits.slice(splitIndex)}`;
};

/**
 * Display phone as "+{country_code} {group1} {group2}".
 * Prefer API `country_code` + national `phone`; fall back to parsing `phone_normalized`.
 */
export const getDisplayPhone = (contact) => {
  if (!contact) return '-';

  let countryDigits = contact.country_code
    ? String(contact.country_code).replace(/[^\d]/g, '')
    : '';
  // API `phone` is the national number (country code already separate).
  let localDigits = contact.phone
    ? String(contact.phone).replace(/[^\d]/g, '')
    : '';

  // Fall back to phone_normalized: parse, or strip country_code from the E.164 value.
  if ((!countryDigits || !localDigits) && contact.phone_normalized) {
    const normalizedRaw = String(contact.phone_normalized).trim();
    try {
      const parsed = parsePhoneNumber(normalizedRaw);
      if (parsed) {
        if (!countryDigits) countryDigits = parsed.countryCallingCode;
        if (!localDigits) localDigits = parsed.nationalNumber;
      }
    } catch {
      // fall through to manual strip below
    }

    if (!localDigits) {
      const normalizedDigits = normalizedRaw.replace(/[^\d]/g, '');
      if (countryDigits && normalizedDigits.startsWith(countryDigits)) {
        localDigits = normalizedDigits.slice(countryDigits.length);
      }
    }
  }

  if (!countryDigits && !localDigits) {
    return contact.phone_normalized || contact.phone || '-';
  }

  const formattedLocal = formatLocalPhoneGroups(localDigits);
  if (countryDigits) {
    return formattedLocal
      ? `+${countryDigits} ${formattedLocal}`
      : `+${countryDigits}`;
  }
  return formattedLocal || '-';
};

/**
 * Clean phone_normalized for Conversation API `search`.
 * Strips a leading '+' so "+918347725082" → "918347725082".
 * Returns null when phone_normalized is missing/empty.
 */
export const getConversationSearchPhone = (contact) => {
  const phone = contact?.phone_normalized;
  if (phone == null || phone === '') return null;

  const cleaned = String(phone).trim().replace(/^\+/, '');
  return cleaned || null;
};

export const parsePhoneForForm = (contact) => {
  if (!contact) return { countryCode: getDefaultCountry(), phoneInput: '' };

  // Prefer parsing the full E.164 number: libphonenumber-js can disambiguate
  // countries that share a calling code (e.g. +1 covers US, Jamaica, Bahamas, ...)
  // by matching the national number's area code, whereas country_code alone cannot.
  const fullNumber =
    contact.phone_normalized ||
    (contact.country_code && contact.phone
      ? `${contact.country_code}${contact.phone}`
      : contact.phone);
  const rawFullNumber = String(fullNumber || '').trim();

  if (rawFullNumber) {
    try {
      const defaultCountry = rawFullNumber.startsWith('+')
        ? undefined
        : getDefaultCountry();
      const parsed = parsePhoneNumber(rawFullNumber, defaultCountry);
      if (parsed) {
        return {
          countryCode:
            parsed.country || getIsoCodeFromCallingCode(contact.country_code),
          phoneInput: contact.phone || parsed.nationalNumber || parsed.number,
        };
      }
    } catch {
      // fall through to the calling-code-only fallback below
    }
  }

  if (contact.country_code && contact.phone) {
    return {
      countryCode: getIsoCodeFromCallingCode(contact.country_code),
      phoneInput: contact.phone,
    };
  }

  const rawPhone = String(contact.phone || '').trim();
  return {
    countryCode: getDefaultCountry(),
    phoneInput: rawPhone.replace(/[^\d+]/g, ''),
  };
};

export const buildPhonePayload = (countryCode, phoneInput) => {
  if (!phoneInput) return '';

  const cleanInput = phoneInput.replace(/[^\d+]/g, '');

  if (cleanInput.startsWith('+')) {
    // It's already fully formatted, just return it
    return cleanInput;
  }

  // If no '+', we prepend the selected country calling code
  try {
    const callingCode = getCountryCallingCode(countryCode);
    return `+${callingCode}${cleanInput}`;
  } catch {
    return `+${cleanInput}`;
  }
};
